import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Line, Area, AreaConfig } from '@ant-design/charts';
import { Skeleton, Typography, Tooltip } from 'antd';
import AntIcon from '@ant-design/icons';
import Paper from '../Paper/Paper';
import styles from './DashboardAreaChartWidget.module.less';
import InfoIcon from '../../assets/img/infoIcon.svg?react';

import { getTimeStringFromSeconds } from '../../utils/dates';

interface ChartLegend {
  total: number | string;
  color: string;
  subtitle?: string;
}

dayjs.extend(utc);

interface DashboardLineChartWidgetProps {
  isError: boolean;
  isLoading: boolean;
  chartTitle: string;
  data: Record<string, any>[];
  legend: ChartLegend[];
  color: string | string[];
  areaStylerCallback: (dataItem: Record<string, any>) => { fill: string };
  seriesField?: string;
  xField?: string;
  yField?: string;
  tooltipText?: string;
  useLine?: boolean;
  timeFormat?: string;
  tooltipFormat?: 'min' | 'km';
}

const tooltipFormatter = {
  km: (obj: Record<string, any>) => ({
    name: ``,
    value: `${((obj.value || 0) / 1000).toFixed(1)} kms`
  }),
  min: (obj: Record<string, any>) => ({
    name: ``,
    value: `${getTimeStringFromSeconds(obj.value)} mins`
  })
};

const yAxisLabelFormatter = {
  km: (text: string) => `${((+text || 0) / 1000).toFixed(1)}`,
  min: (text: string) => getTimeStringFromSeconds(+text)
};

const getAreaConfig = (
  data: Record<string, any>[],
  color: string | string[],
  areaStylerCallback: (dataItem: Record<string, any>) => { fill: string },
  seriesField?: string,
  xField?: string,
  yField?: string,
  timeFormat?: string,
  tooltipFormat?: 'min' | 'km'
): AreaConfig => {
  //@ts-ignore
  return {
    data,
    seriesField,
    color,
    label: false,
    legend: false,
    xField: xField || 'timestamp',
    yField: yField || 'value',
    xAxis: {
      range: [0, 1],
      sync: true
    },
    areaStyle: areaStylerCallback,
    point: {
      shape: 'circle',
      state: undefined
    },
    height: 150,
    meta: {
      timestamp: {
        formatter: (value: string) => {
          dayjs(value)
            .utc(true)
            .local()
            .format(timeFormat || 'MMM D, h a');
        }
      },
      value: {
        formatter: yAxisLabelFormatter[tooltipFormat || 'min']
      }
    },
    yAxis: {
      sync: true
    },
    tooltip: {
      domStyles: {
        'g2-tooltip': {
          'background-color': 'rgb(0,0,0, 0.8)',
          color: 'rgb(0,0,0, 0.0)'
        },
        'g2-tooltip-marker': {
          display: 'none'
        },
        'g2-tooltip-title': {
          color: 'rgb(255,255,255)'
        },
        'g2-tooltip-value': {
          color: 'rgb(255,255,255)',
          'margin-left': '-5px',
          'font-weight': 'bold'
        }
      },
      formatter: tooltipFormatter[tooltipFormat || 'min']
    }
  };
};

const getChartLegend = (legend: ChartLegend[], isError: boolean) => {
  const legendArr = legend.map(legendItem => (
    <div className={styles.wrapper} key={`${legendItem.subtitle}`}>
      {legendItem.subtitle && <Typography.Text type={'secondary'}>{legendItem.subtitle}</Typography.Text>}
      <div className={styles.widgetRow}>
        <Typography.Text style={{ color: legendItem.color }} className={styles.chartTotalText}>
          {isError ? 'n/a' : legendItem.total}
        </Typography.Text>
      </div>
    </div>
  ));

  return <div className={styles.legendContainer}>{legendArr}</div>;
};

export const DashboardAreaChartWidget = ({
  isError,
  isLoading,
  chartTitle,
  data,
  legend,
  seriesField,
  color,
  areaStylerCallback,
  xField,
  yField,
  tooltipText,
  useLine = false,
  timeFormat,
  tooltipFormat
}: DashboardLineChartWidgetProps) => {
  const areaChartConfig = getAreaConfig(
    data,
    color,
    areaStylerCallback,
    seriesField,
    xField,
    yField,
    timeFormat,
    tooltipFormat
  );
  const chartLegend = getChartLegend(legend, isError);

  const chartComponent = useLine ? <Line {...areaChartConfig} /> : <Area {...areaChartConfig} />;

  return (
    <Paper data-testid="dashboardAreaChartWidget">
      {!isLoading ? (
        <div className={styles.wrapper}>
          <div className={styles.widgetRow}>
            <Typography.Text className={styles.chartTitleText}>{chartTitle}</Typography.Text>
            {tooltipText && (
              <Tooltip style={{ background: '#FFFFFF', color: 'black' }} title={tooltipText} placement="top">
                <AntIcon style={{ fontSize: 22 }} component={() => <InfoIcon />} />
              </Tooltip>
            )}
          </div>
          {chartLegend}
          {!isError && chartComponent}
        </div>
      ) : (
        <Skeleton active paragraph={{ rows: 8 }} />
      )}
    </Paper>
  );
};

export default DashboardAreaChartWidget;
